import { useEffect, useState } from "react";
import { X, ChevronRight, ChevronLeft, GraduationCap, Target, Trophy, Rocket } from "lucide-react";

const steps = [
  {
    icon: Rocket,
    title: "Welcome to Your Journey!",
    description: "You're about to become a young entrepreneur! This program will teach you how to run your own lemonade stand business.",
    tip: "Take your time with each lesson - there's no rush!"
  },
  {
    icon: Target,
    title: "How to Get Started",
    description: "Start with Session 1 below. Each session has 3 lessons and a video to watch. Complete them in order to unlock your progress.",
    tip: "Watch the intro video first - it sets up each session!"
  },
  {
    icon: GraduationCap,
    title: "Track Your Progress",
    description: "As you complete lessons, you'll earn points and badges. Your progress bar shows how far you've come through all 21 lessons.",
    tip: "Check back often to see your achievements grow!"
  },
  {
    icon: Trophy,
    title: "Earn Your Certificate",
    description: "Complete all 7 sessions to unlock your official completion certificate! You can download and share it with family and friends.",
    tip: "You've got this! 🍋"
  }
];

function QuickStartGuide({ onClose, userName }) {
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    localStorage.setItem("quickStartCompleted", "true");
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem("quickStartCompleted", "true");
    onClose();
  };

  // Esc-to-close (treats as Skip — same persistence behavior). Listen on
  // window so the modal closes even when focus is inside a child button.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        handleSkip();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = steps[currentStep];
  const Icon = step.icon;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quickstart-title"
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-400 p-6 relative">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            aria-label="Skip guide"
            data-testid="quickstart-skip-btn"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🍋</span>
            <h2 id="quickstart-title" className="text-xl font-bold text-white">
              Quick Start Guide
            </h2>
          </div>
          <p className="text-white/90 text-sm">
            Welcome{userName ? `, ${userName.split(" ")[0]}` : ""}! Let&apos;s get you started.
          </p>
          
          {/* Progress dots */}
          <div className="flex gap-2 mt-4">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentStep 
                    ? "w-8 bg-white" 
                    : index < currentStep 
                      ? "w-2 bg-white/80" 
                      : "w-2 bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-100 to-orange-100 flex items-center justify-center mb-4">
              <Icon className="w-10 h-10 text-orange-500" />
            </div>
            
            <h3 className="text-2xl font-bold text-gray-800 mb-3">
              {step.title}
            </h3>
            
            <p className="text-gray-600 leading-relaxed mb-4">
              {step.description}
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800">
              💡 <span className="font-medium">Tip:</span> {step.tip}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                currentStep === 0
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              data-testid="quickstart-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            <span className="text-sm text-gray-400">
              {currentStep + 1} of {steps.length}
            </span>

            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-6 py-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-lg font-semibold hover:from-yellow-500 hover:to-orange-500 transition-all shadow-md hover:shadow-lg"
              data-testid="quickstart-next-btn"
            >
              {currentStep === steps.length - 1 ? "Let's Go!" : "Next"}
              {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuickStartGuide;
